import { safeExecute, } from "@opendaw/lib-std";
import { Box, NoPointers, PointerField, StringField, BooleanField, Float32Field, Int32Field, ArrayField, } from "@opendaw/lib-box";
import { TubularOperator } from "./TubularOperator";
import { TubularLfo } from "./TubularLfo";
import { TubularPitchEnvelope } from "./TubularPitchEnvelope";
import { Pointers } from "@opendaw/studio-enums";
export class TubularDeviceBox extends Box {
    static create(graph, uuid, constructor) {
        return graph.stageBox(new TubularDeviceBox({
            uuid,
            graph,
            name: "TubularDeviceBox",
            pointerRules: {
                accepts: [
                    Pointers.Device,
                    Pointers.Selection,
                    Pointers.MetaData,
                    Pointers.SideChain,
                ],
                mandatory: false,
                exclusive: false,
            },
            resource: undefined,
            ephemeral: false,
        }), constructor);
    }
    static ClassName = "TubularDeviceBox";
    static Resource = undefined;
    static Ephemeral = false;
    static Tags = Object.freeze({
        type: "device",
        deviceType: "instrument",
        content: "notes",
    });
    constructor(construct) {
        super(construct);
    }
    accept(visitor) {
        return safeExecute(visitor.visitTubularDeviceBox, this);
    }
    get tags() {
        return TubularDeviceBox.Tags;
    }
    get host() {
        return this.getField(1);
    }
    get label() {
        return this.getField(2);
    }
    get icon() {
        return this.getField(3);
    }
    get enabled() {
        return this.getField(4);
    }
    get minimized() {
        return this.getField(5);
    }
    get cutoff() {
        return this.getField(10);
    }
    get resonance() {
        return this.getField(11);
    }
    get volume() {
        return this.getField(12);
    }
    get voicingMode() {
        return this.getField(13);
    }
    get tune() {
        return this.getField(14);
    }
    get algorithm() {
        return this.getField(15);
    }
    get feedback() {
        return this.getField(16);
    }
    get oscKeySync() {
        return this.getField(17);
    }
    get lfo() {
        return this.getField(20);
    }
    get pitchModSens() {
        return this.getField(21);
    }
    get transpose() {
        return this.getField(22);
    }
    get pitchEnvelope() {
        return this.getField(30);
    }
    get operators() {
        return this.getField(40);
    }
    get voiceLoad() {
        return this.getField(50);
    }
    /** @deprecated */
    get engine() {
        return this.getField(51);
    }
    initializeFields() {
        return {
            1: PointerField.create({
                parent: this,
                fieldKey: 1,
                fieldName: "host",
                deprecated: false,
                pointerRules: NoPointers,
            }, Pointers.InstrumentHost, true),
            2: StringField.create({
                parent: this,
                fieldKey: 2,
                fieldName: "label",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            3: StringField.create({
                parent: this,
                fieldKey: 3,
                fieldName: "icon",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            4: BooleanField.create({
                parent: this,
                fieldKey: 4,
                fieldName: "enabled",
                deprecated: false,
                pointerRules: NoPointers,
            }, true),
            5: BooleanField.create({
                parent: this,
                fieldKey: 5,
                fieldName: "minimized",
                deprecated: false,
                pointerRules: NoPointers,
            }, false),
            10: Float32Field.create({
                parent: this,
                fieldKey: 10,
                fieldName: "cutoff",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 1),
            11: Float32Field.create({
                parent: this,
                fieldKey: 11,
                fieldName: "resonance",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 0),
            12: Float32Field.create({
                parent: this,
                fieldKey: 12,
                fieldName: "volume",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 1),
            13: Int32Field.create({
                parent: this,
                fieldKey: 13,
                fieldName: "voicingMode",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { values: [0, 1] }, "", 1),
            14: Float32Field.create({
                parent: this,
                fieldKey: 14,
                fieldName: "tune",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: -100, max: 100, scaling: "linear" }, "ct", 0),
            15: Int32Field.create({
                parent: this,
                fieldKey: 15,
                fieldName: "algorithm",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 31 }, "", 0),
            16: Int32Field.create({
                parent: this,
                fieldKey: 16,
                fieldName: "feedback",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 7 }, "", 0),
            17: Int32Field.create({
                parent: this,
                fieldKey: 17,
                fieldName: "oscKeySync",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 1 }, "", 1),
            20: TubularLfo.create({
                parent: this,
                fieldKey: 20,
                fieldName: "lfo",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            21: Int32Field.create({
                parent: this,
                fieldKey: 21,
                fieldName: "pitchModSens",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 7 }, "", 3),
            22: Int32Field.create({
                parent: this,
                fieldKey: 22,
                fieldName: "transpose",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 48 }, "", 24),
            30: TubularPitchEnvelope.create({
                parent: this,
                fieldKey: 30,
                fieldName: "pitchEnvelope",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            40: ArrayField.create({
                parent: this,
                fieldKey: 40,
                fieldName: "operators",
                deprecated: false,
                pointerRules: NoPointers,
            }, (construct) => TubularOperator.create(construct), 6),
            50: Int32Field.create({
                parent: this,
                fieldKey: 50,
                fieldName: "voiceLoad",
                deprecated: false,
                pointerRules: NoPointers,
            }, "index", "", 0),
            51: Int32Field.create({
                parent: this,
                fieldKey: 51,
                fieldName: "engine",
                deprecated: true,
                pointerRules: NoPointers,
            }, { min: 0, max: 1 }, "", 1),
        };
    }
}
